// src/index.ts
export * from "@chakra-ui/react";

// ../utils/dist/index.mjs
import { format } from "date-fns/format";
import { nb } from "date-fns/locale/nb";
var colors = {
  black: "#000000",
  grey90: "#181818",
  grey80: "#333333",
  grey60: "#585858",
  grey45: "#8c8c8c",
  grey30: "#b2b2b2",
  grey15: "#d9d9d9",
  grey10: "#e5e5e5",
  grey05: "#f2f2f2",
  white: "#ffffff",
  bsR\u00F8dDark: "#870010",
  bsR\u00F8dMedium: "#d53d40",
  accentPurple: "#331433",
  accentPeach: "#ffba99",
  accentGreen: "#003b29",
  accentBlueDark: "#001740",
  accentBlueMedium: "#5996de",
  accentYellow: "#ffc970",
  lightBlue: "#dbe8ef",
  lightGreen: "#dbe0db",
  lightPeach: "#f4e7de",
  lightPurple: "#ece8f3",
  statusRed: "#ad313c",
  statusYellow: "#bc5600",
  statusGreen: "#24845b",
  statusGreenLight: "#edf4f1",
  statusRedLight: "#f8efef",
  statusYellowLight: "#fff6e4",
  neptune: {
    "100": "#CFE6E8",
    "200": "#9FCDD2",
    "300": "#6FB5BB",
    "400": "#3F9CA5",
    "500": "#0F838E",
    "600": "#0C6972",
    "700": "#094F55",
    "800": "#063439",
    "900": "#031A1C"
  }
};
function isDevelopment() {
  return process.env.NODE_ENV === "development";
}

// src/components/Alert.tsx
import { Box, Flex } from "@chakra-ui/react";
import React from "react";
import { AlertCircle, CheckCircle, Info, XCircle } from "react-feather";
var colorLookup = {
  info: { bg: colors.lightBlue, color: colors.accentBlueMedium },
  warning: { bg: colors.statusYellowLight, color: colors.statusYellow },
  success: { bg: colors.statusGreenLight, color: colors.statusGreen },
  error: { bg: colors.statusRedLight, color: colors.statusRed }
};
var iconLookup = {
  info: /* @__PURE__ */ React.createElement(Info, { strokeWidth: 1.75, role: "img", "aria-label": "Informasjon" }),
  warning: /* @__PURE__ */ React.createElement(AlertCircle, { strokeWidth: 1.75, role: "img", "aria-label": "Advarsel" }),
  success: /* @__PURE__ */ React.createElement(CheckCircle, { strokeWidth: 1.75, role: "img", "aria-label": "Suksess" }),
  error: /* @__PURE__ */ React.createElement(XCircle, { strokeWidth: 1.75, role: "img", "aria-label": "Feil" })
};
var variantStyles = {
  inline: {
    borderColor: "transparent",
    backgroundColor: "transparent",
    padding: "0"
  }
};
function Alert({ status, children, variant, ...rest }) {
  const colors2 = colorLookup[status];
  return /* @__PURE__ */ React.createElement(
    Flex,
    {
      role: "alert",
      flexWrap: "wrap",
      alignItems: "center",
      gridGap: "0.5em 0.6em",
      background: "white",
      border: `0.1rem solid`,
      borderColor: colors2.color,
      backgroundColor: colors2.bg,
      padding: "0.75rem",
      borderRadius: "0.3rem",
      ...variant ? variantStyles[variant] : {},
      ...rest
    },
    /* @__PURE__ */ React.createElement(Box, { color: colors2.color, flex: "0 0", marginLeft: "auto", marginRight: "auto", lineHeight: "1", fontSize: "1.5em" }, iconLookup[status]),
    /* @__PURE__ */ React.createElement(Box, { flex: "1" }, children)
  );
}

// src/components/Button.tsx
import { Button as ChakraButton } from "@chakra-ui/react";
var Button = ChakraButton;

// src/components/ConditionalWrapper.tsx
import React2 from "react";
var ConditionalWrapper = ({ condition, children, wrapper: Wrapper }) => condition ? /* @__PURE__ */ React2.createElement(Wrapper, null, children) : /* @__PURE__ */ React2.createElement(React2.Fragment, null, children);

// src/components/HideWithCSS.tsx
import { Box as Box2 } from "@chakra-ui/react";
import React3 from "react";
var HideWithCSS = ({ children, above, below, ...chakraProps }) => {
  const display = {
    ...!!below ? { base: "none", [below]: "block" } : {},
    ...!!above ? { [above]: "none" } : {}
  };
  return /* @__PURE__ */ React3.createElement(Box2, { ...chakraProps, display }, children);
};

// src/components/Input.tsx
import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input as ChakraInput
} from "@chakra-ui/react";
import React5, { forwardRef } from "react";

// src/components/VisuallyHidden.tsx
import React4, { useEffect } from "react";
import { VisuallyHidden as ChakraVisuallyHidden } from "@chakra-ui/react";
function VisuallyHidden({ children, ...rest }) {
  const [forceShow, setForceShow] = React4.useState(false);
  useEffect(() => {
    if (isDevelopment()) {
      const handleKeyDown = (ev) => {
        if (ev.key === "Alt") {
          setForceShow(true);
        }
      };
      const handleKeyUp = (ev) => {
        if (ev.key === "Alt") {
          setForceShow(false);
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("keyup", handleKeyUp);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
        window.removeEventListener("keyup", handleKeyUp);
      };
    }
  }, []);
  return forceShow ? /* @__PURE__ */ React4.createElement("span", null, children) : /* @__PURE__ */ React4.createElement(ChakraVisuallyHidden, { position: "fixed" }, children);
}

// src/components/Input.tsx
var Input = forwardRef((props, ref) => {
  const { labelProps, label, helperText, errorMessage, hideLabel, ...inputProps } = props;
  const formLabel = /* @__PURE__ */ React5.createElement(FormLabel, { ...labelProps }, label);
  return /* @__PURE__ */ React5.createElement(FormControl, { isInvalid: !!errorMessage }, hideLabel ? /* @__PURE__ */ React5.createElement(VisuallyHidden, null, formLabel) : formLabel, helperText && /* @__PURE__ */ React5.createElement(FormHelperText, { marginTop: "-0.25em", marginBottom: "0.25em" }, helperText), /* @__PURE__ */ React5.createElement(ChakraInput, { ref, ...inputProps }), errorMessage && /* @__PURE__ */ React5.createElement(FormErrorMessage, { color: colors.statusRed, fontWeight: 600 }, errorMessage));
});

// src/components/Link.tsx
import { Link as ChakraLink } from "@chakra-ui/react";
var Link = ChakraLink;

// src/BiblioteksentralenProvider.tsx
import { ChakraProvider, extendTheme } from "@chakra-ui/react";
import React6 from "react";

// src/styles/chakraTheme/ButtonStyles.ts
var variants = {
  primary: {
    backgroundColor: colors.black,
    color: "white",
    _hover: {
      backgroundColor: "hsla(0deg, 0%, 70%, 1)",
      color: "black"
    }
  },
  secondary: {
    borderColor: "currentColor",
    backgroundColor: "transparent",
    color: "currentColor",
    _hover: {
      backgroundColor: "hsla(0deg, 0%, 50%, 0.15)"
    }
  },
  tertiary: {
    backgroundColor: "transparent",
    color: "currentColor",
    _hover: {
      backgroundColor: "hsla(0deg, 0%, 50%, 0.15)",
      color: "currentColor"
    }
  }
};
var ButtonStyles = {
  baseStyle: {
    border: "transparent 0.1em solid",
    borderRadius: "0.35em",
    fontWeight: 600,
    _disabled: {
      backgroundColor: `${colors.grey45} !important`,
      color: "white !important",
      opacity: 1
    }
  },
  sizes: {
    sm: {
      padding: "0.2em 0.5em",
      fontSize: "1rem"
    }
  },
  variants,
  defaultProps: {
    variant: "primary"
  }
};

// src/styles/chakraTheme/CheckboxStyles.ts
import { createMultiStyleConfigHelpers } from "@chakra-ui/react";
import { checkboxAnatomy } from "@chakra-ui/anatomy";
var { definePartsStyle, defineMultiStyleConfig: defineMultiStyleConfigWithTypeIssue } = createMultiStyleConfigHelpers(
  checkboxAnatomy.keys
);
var defineMultiStyleConfig = defineMultiStyleConfigWithTypeIssue;
var baseStyle = definePartsStyle({
  control: {
    background: "white"
  }
});
var CheckboxStyles = defineMultiStyleConfig({ baseStyle });

// src/styles/chakraTheme/ContainerStyles.ts
var ContainerStyles = {
  baseStyle: {
    px: ".75rem"
    // Padding left/right
  }
};

// src/styles/chakraTheme/HeadingStyles.ts
var HeadingStyles = {
  baseStyle: {
    fontWeight: "600"
  }
};

// src/styles/chakraTheme/InputStyles.ts
import { mode } from "@chakra-ui/theme-tools";
var InputStyles = {
  baseStyle: (props) => ({
    field: {
      _placeholder: {
        color: mode("gray.500", "whiteAlpha.500")(props)
      }
    }
  }),
  variants: {
    filled: (props) => ({
      field: {
        bg: mode("gray.200", "whiteAlpha.100")(props),
        _hover: {
          bg: mode("gray.300", "whiteAlpha.200")(props)
        }
      }
    }),
    outline: (props) => ({
      field: {
        borderColor: mode("gray.300", "whiteAlpha.300")(props),
        _hover: {
          borderColor: mode("gray.400", "whiteAlpha.400")(props)
        }
      }
    }),
    flushed: (props) => ({
      field: {
        borderColor: mode("gray.300", "whiteAlpha.300")(props),
        _hover: {
          borderColor: mode("gray.400", "whiteAlpha.400")(props)
        }
      }
    })
  },
  defaultProps: {
    variant: "filled"
  }
};

// src/styles/chakraTheme/LinkStyles.ts
var variants2 = {
  plain: {
    textDecoration: "none",
    _hover: {
      textDecoration: "underline"
    }
  }
};
var LinkStyles = {
  baseStyle: {
    textDecoration: "underline",
    _hover: {
      textDecoration: "none"
    }
  },
  variants: variants2
};

// src/styles/chakraTheme/ModalStyles.ts
var ModalStyles = {
  baseStyle: {
    // Fix for modal height on Safari iOS:
    // https://github.com/chakra-ui/chakra-ui/issues/4680#issuecomment-1301640929
    dialogContainer: {
      "@supports(height: -webkit-fill-available)": {},
      height: "100%"
    }
  }
};

// src/styles/chakraTheme/SpinnerStyles.ts
import { Spinner } from "@chakra-ui/react";
var SpinnerStyles = {};
Spinner.defaultProps = {
  ...Spinner.defaultProps,
  speed: ".8s",
  color: colors.accentBlueMedium,
  emptyColor: "rgba(0,0,0,.2)",
  thickness: ".175em"
};

// src/styles/chakraTheme/sizes.ts
var container = {
  sm: "40rem",
  md: "48rem",
  lg: "56rem",
  // Forsøker 56rem som standardbredde etter testing med Katrine
  xl: "80rem"
};
var sizes = {
  container
};

// src/styles/chakraTheme/biblioteksentralenChakraTheme.ts
var biblioteksentralenChakraTheme = {
  styles: {
    global: {
      html: {
        fontSize: { base: "112.5%", md: "120%" },
        // Sørger for smooth scrolling for interne lenker som scroller til annet sted på siden,
        // men ikke hvis fokus er utenfor viduet, feks ved søk i tekst (ctrl + f)
        "&:focus-within": { scrollBehavior: "smooth !important" }
      }
    }
  },
  components: {
    Heading: HeadingStyles,
    Spinner: SpinnerStyles,
    Link: LinkStyles,
    Button: ButtonStyles,
    Input: InputStyles,
    Container: ContainerStyles,
    Modal: ModalStyles,
    Checkbox: CheckboxStyles
  },
  sizes
};

// src/BiblioteksentralenProvider.tsx
var emptyTheme = {};
var BiblioteksentralenProvider = (props) => /* @__PURE__ */ React6.createElement(ChakraProvider, { theme: extendTheme(biblioteksentralenChakraTheme, props.customTheme ?? emptyTheme) }, props.children);

// src/components/ErrorBoundary.tsx
import { Box as Box3 } from "@chakra-ui/react";
import React7 from "react";
var StyledPre = (props) => /* @__PURE__ */ React7.createElement(Box3, { as: "pre", paddingTop: "0.5rem", wordBreak: "break-all", whiteSpace: "pre-wrap", fontSize: "0.8rem", ...props });
var ErrorBoundary = class extends React7.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    this.setState({ hasError: true, error, errorInfo });
    console.error(error, { errorInfo, boundaryName: this.props.boundaryName });
  }
  render() {
    if (this.state.hasError) {
      const stackTrace = this.state.errorInfo?.componentStack;
      const errormsg = this.state.error?.message;
      const info = this.props.boundaryName;
      return /* @__PURE__ */ React7.createElement("div", null, /* @__PURE__ */ React7.createElement(Alert, { status: "error" }, /* @__PURE__ */ React7.createElement("p", null, "Beklager, det skjedde en teknisk feil."), isDevelopment() && (stackTrace || errormsg) && /* @__PURE__ */ React7.createElement("div", null, /* @__PURE__ */ React7.createElement(StyledPre, null, errormsg || ""), /* @__PURE__ */ React7.createElement(StyledPre, null, info || ""), /* @__PURE__ */ React7.createElement(StyledPre, null, stackTrace || ""))));
    }
    return this.props.children;
  }
};

// src/components/HashLinkTarget.tsx
import { Box as Box4 } from "@chakra-ui/react";
import React8 from "react";
var focusOnRelativeParentStyle = {
  "&:focus-within": {
    position: "static",
    boxShadow: "none",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      height: "100%",
      width: "100%",
      pointerEvents: "none",
      borderRadius: "0.25rem",
      boxShadow: "var(--chakra-shadows-outline)"
    }
  }
};
function HashLinkTarget(props) {
  return /* @__PURE__ */ React8.createElement(Box4, { position: "relative", sx: props.focusOnParent ? focusOnRelativeParentStyle : void 0 }, /* @__PURE__ */ React8.createElement(
    Box4,
    {
      id: props.id,
      tabIndex: props.focusOnParent ? -1 : void 0,
      position: "absolute",
      top: `-${props.spaceAbove || "2.5rem"}`,
      _focus: {
        boxShadow: "none !important"
      }
    }
  ));
}

// src/components/withErrorBoundary.tsx
import React9 from "react";
var withErrorBoundary = (Component, boundaryName) => {
  return React9.forwardRef((props, ref) => /* @__PURE__ */ React9.createElement(ErrorBoundary, { boundaryName }, /* @__PURE__ */ React9.createElement(Component, { ...props, ref })));
};
export {
  Alert,
  BiblioteksentralenProvider,
  Button,
  ConditionalWrapper,
  ErrorBoundary,
  HashLinkTarget,
  HideWithCSS,
  Input,
  Link,
  VisuallyHidden,
  biblioteksentralenChakraTheme,
  colors,
  withErrorBoundary
};
//# sourceMappingURL=index.mjs.map